import { useEffect, useRef, useState } from 'react'
import { Loader2, Copy, Check, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { mfaEnrollTotp, mfaChallenge, mfaVerify, mfaListFactors, mfaUnenroll } from '@/lib/supabase'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import { captureEvent } from '@/lib/posthog'
import { MfaRecoveryCodesDisplay } from './MfaRecoveryCodesDisplay'

type Step = 'intro' | 'scan' | 'codes'

interface MfaEnrollDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onEnrolled?: () => void
}

interface MfaEnrolledResponse {
  enrolled: boolean
  enrolledAt: string | null
  codes: string[]
  remaining: number
  alreadyIssued?: boolean
}

const SUGGESTED_APPS = [
  'Google Authenticator',
  'Microsoft Authenticator',
  'Authy',
  '1Password',
  'Bitwarden',
  'Apple Passwords',
  'Duo Mobile',
]

export function MfaEnrollDialog({ open, onOpenChange, onEnrolled }: MfaEnrollDialogProps) {
  const user = useAuthStore((s) => s.user)
  const setMfaStatus = useAuthStore((s) => s.setMfaStatus)
  const refreshAal = useAuthStore((s) => s.refreshAal)

  const [step, setStep] = useState<Step>('intro')
  const [factorId, setFactorId] = useState<string | null>(null)
  const [qrCode, setQrCode] = useState<string | null>(null)
  const [secret, setSecret] = useState<string | null>(null)
  const [showSecret, setShowSecret] = useState(false)
  const [secretCopied, setSecretCopied] = useState(false)
  const [code, setCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const completedRef = useRef(false)

  useEffect(() => {
    if (open) {
      // Fresh enrollment session — clear the "completed" sentinel so the
      // unenroll-on-cancel cleanup can fire if the user abandons mid-flow.
      // Reset on OPEN (not close) because resetting it on close races the
      // [factorId] cleanup: setFactorId(null) below triggers that cleanup,
      // and if completedRef.current was already reset it would call
      // mfaUnenroll on the just-verified factor — deleting the Supabase
      // factor while leaving mfa_enrolled_at set in our DB. Orphan state.
      completedRef.current = false
    } else {
      // Reset visible state so the next open starts clean. Do NOT touch
      // completedRef here (see comment above).
      setStep('intro')
      setFactorId(null)
      setQrCode(null)
      setSecret(null)
      setShowSecret(false)
      setCode('')
      setRecoveryCodes([])
      setError(null)
    }
  }, [open])

  // Cancel cleanup — if the user closes after Supabase enroll succeeded but
  // before verify, unenroll the orphan factor (best effort). Failures are
  // non-blocking; the next enroll's reconciliation step (B5) handles them.
  useEffect(() => {
    return () => {
      if (factorId && !completedRef.current) {
        void mfaUnenroll(factorId).catch(() => {})
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factorId])

  const startEnroll = async () => {
    setIsLoading(true)
    setError(null)
    try {
      // B5 + G8 — reconcile orphan factors before issuing a fresh enroll.
      // Read from `data.all` rather than `data.totp` because Supabase's
      // narrowed type claims `totp[]` is verified-only, but at runtime it
      // contains both verified and unverified entries.
      const factors = await mfaListFactors()
      const totpFactors = (factors?.all ?? []).filter((f) => f.factor_type === 'totp')
      const verified = totpFactors.find((f) => f.status === 'verified')
      if (verified) {
        // Supabase has a verified factor but our backend missed the post-verify
        // POST. Skip enrollment, reconcile, jump to recovery codes.
        const result = await api.post<MfaEnrolledResponse>('/users/me/mfa-enrolled', {})
        if (result.codes.length > 0) {
          setRecoveryCodes(result.codes)
          setMfaStatus({
            enrolled: true,
            enrolledAt: result.enrolledAt,
            recoveryCodesRemaining: result.remaining,
            required: useAuthStore.getState().mfaRequired,
          })
          completedRef.current = true
          setStep('codes')
          await refreshAal()
          captureEvent('mfa_enrolled', { method: 'totp', reconciled: true })
          return
        }
        // Already had codes — nothing to show. Treat as "already enrolled" and
        // close the dialog.
        toast.success('Two-factor authentication is already enabled')
        onOpenChange(false)
        return
      }

      // Clean up unverified orphans before issuing a new enroll.
      const unverified = totpFactors.filter((f) => f.status === 'unverified')
      for (const orphan of unverified) {
        await mfaUnenroll(orphan.id).catch(() => {})
      }

      const data = await mfaEnrollTotp()
      setFactorId(data.id)
      setQrCode(data.totp.qr_code)
      setSecret(data.totp.secret)
      setStep('scan')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start enrollment')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerify = async () => {
    if (!factorId || code.length !== 6) return
    setIsLoading(true)
    setError(null)
    try {
      const challenge = await mfaChallenge(factorId)
      await mfaVerify(factorId, challenge.id, code)

      // Persist enrollment + generate recovery codes.
      const result = await api.post<MfaEnrolledResponse>('/users/me/mfa-enrolled', {})
      setRecoveryCodes(result.codes)
      setMfaStatus({
        enrolled: true,
        enrolledAt: result.enrolledAt,
        recoveryCodesRemaining: result.remaining,
        required: useAuthStore.getState().mfaRequired,
      })
      completedRef.current = true
      setStep('codes')
      await refreshAal()
      captureEvent('mfa_enrolled', { method: 'totp' })
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "That code wasn't valid. Codes expire every 30 seconds — try the next one."
      )
      setCode('')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCodeChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 6)
    setCode(digits)
    if (digits.length === 6 && !isLoading) {
      // Auto-submit when 6 digits entered.
      void handleVerify()
    }
  }

  const handleCopySecret = async () => {
    if (!secret) return
    try {
      await navigator.clipboard.writeText(secret)
      setSecretCopied(true)
      setTimeout(() => setSecretCopied(false), 2000)
    } catch {
      // Non-fatal.
    }
  }

  const handleDone = () => {
    onEnrolled?.()
    onOpenChange(false)
    toast.success('Two-factor authentication enabled')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {step === 'intro' && (
          <>
            <DialogHeader>
              <DialogTitle>Set up two-factor authentication</DialogTitle>
              <DialogDescription>
                Add an extra layer of security. You'll enter a 6-digit code from an authenticator
                app each time you sign in from a new device.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <p className="text-meta">
                  Use any standards-compliant authenticator app:
                </p>
                <ul className="text-sm grid grid-cols-2 gap-x-4 gap-y-1">
                  {SUGGESTED_APPS.map((name) => (
                    <li key={name} className="text-muted-foreground">{name}</li>
                  ))}
                </ul>
              </div>
              {error && (
                <div className="flex items-center gap-2 p-3 rounded-lg border border-destructive/30 bg-destructive/10 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="button" onClick={() => void startEnroll()} disabled={isLoading}>
                  {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Continue
                </Button>
              </div>
            </div>
          </>
        )}

        {step === 'scan' && (
          <>
            <DialogHeader>
              <DialogTitle>Scan QR code</DialogTitle>
              <DialogDescription>
                Open your authenticator app and scan the QR code below.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {qrCode && (
                <div className="flex justify-center">
                  <img
                    src={qrCode}
                    alt="TOTP QR code"
                    className="w-48 h-48 bg-white p-2 rounded-lg border"
                  />
                </div>
              )}

              <div>
                <button
                  type="button"
                  onClick={() => setShowSecret((v) => !v)}
                  className="text-meta hover:underline"
                >
                  {showSecret ? "Hide secret" : "Can't scan? Show secret"}
                </button>
                {showSecret && secret && (
                  <div className="mt-2 flex items-center gap-2 p-2 rounded-lg bg-muted font-mono text-xs break-all">
                    <span className="flex-1">{secret}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => void handleCopySecret()}
                    >
                      {secretCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="totp-code" className="text-meta">
                  Enter the verification code from your authenticator app
                </Label>
                <Input
                  id="totp-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => handleCodeChange(e.target.value)}
                  className="text-center text-lg tracking-widest font-mono"
                  autoFocus
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 rounded-lg border border-destructive/30 bg-destructive/10 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => void handleVerify()}
                  disabled={code.length !== 6 || isLoading}
                >
                  {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Verify
                </Button>
              </div>
            </div>
          </>
        )}

        {step === 'codes' && (
          <>
            <DialogHeader>
              <DialogTitle>Save your recovery codes</DialogTitle>
            </DialogHeader>
            <MfaRecoveryCodesDisplay
              codes={recoveryCodes}
              email={user?.email ?? ''}
              onConfirm={handleDone}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
